import * as React from "react";
import { Inbox, SearchX, AlertTriangle, Plus } from "lucide-react";
import { cn } from "lib/utils";
import { Button } from "components/ui/button";
import { IconTile } from "components/ui/icon-tile";
import type { Tone } from "components/ui/tone";

export type EmptyStateVariant = "empty" | "no-results" | "error";

export interface EmptyStateAction {
    label: string;
    onClick?: () => void;
    icon?: React.ReactNode;
    /** Render as a link instead of a button */
    href?: string;
}

export interface EmptyStateProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
    /**
     * `empty`      — nothing exists yet, so lead with the create action
     * `no-results` — filters excluded everything, so lead with "clear filters"
     * `error`      — the request failed, so lead with retry
     */
    variant?: EmptyStateVariant;
    icon?: React.ElementType;
    title: React.ReactNode;
    description?: React.ReactNode;
    tone?: Tone;
    primaryAction?: EmptyStateAction | null;
    secondaryAction?: EmptyStateAction | null;
    /** Optional "here's what you can do" tips shown under the actions */
    hints?: Array<{ icon?: React.ElementType; title: string; description?: string }>;
    /** Removes the card chrome so it can sit inside an existing panel */
    bare?: boolean;
    size?: "sm" | "md" | "lg";
}

const DEFAULTS: Record<EmptyStateVariant, { icon: React.ElementType; tone: Tone }> = {
    empty: { icon: Inbox, tone: "brand" },
    "no-results": { icon: SearchX, tone: "neutral" },
    error: { icon: AlertTriangle, tone: "danger" },
};

const PAD = { sm: "px-5 py-8", md: "px-6 py-12", lg: "px-6 py-16" } as const;

function ActionButton({
    action,
    variant,
}: {
    action: EmptyStateAction;
    variant: "primary" | "secondary";
}) {
    if (action.href) {
        return (
            <Button asChild variant={variant}>
                <a href={action.href}>
                    {action.icon}
                    {action.label}
                </a>
            </Button>
        );
    }
    return (
        <Button variant={variant} onClick={action.onClick}>
            {action.icon}
            {action.label}
        </Button>
    );
}

/**
 * One empty state for the whole app. Replaces `templates/EmptyState`, the
 * various `support` placeholders and dozens of inline "No X found" blocks —
 * none of which had dark-mode styling or an accessible status role.
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
    variant = "empty",
    icon,
    title,
    description,
    tone: toneOverride,
    primaryAction,
    secondaryAction,
    hints,
    bare = false,
    size = "md",
    className,
    children,
    ...props
}) => {
    const preset = DEFAULTS[variant] ?? DEFAULTS.empty;
    const Icon = icon ?? preset.icon;
    const t = toneOverride ?? preset.tone;

    return (
        <div
            role={variant === "error" ? "alert" : "status"}
            className={cn(
                "w-full text-center flex flex-col items-center",
                PAD[size],
                !bare && "rounded-xl bg-surface border border-line shadow-card",
                className
            )}
            {...props}
        >
            <IconTile tone={t} size="xl" className="mb-4">
                <Icon strokeWidth={1.75} />
            </IconTile>

            <h3 className="text-title font-semibold text-content mb-1.5 max-w-prose">{title}</h3>

            {description && (
                <p className="text-ui-lg text-content-muted leading-relaxed max-w-md mb-6">
                    {description}
                </p>
            )}

            {(primaryAction || secondaryAction) && (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2.5 w-full sm:w-auto">
                    {primaryAction && <ActionButton action={primaryAction} variant="primary" />}
                    {secondaryAction && <ActionButton action={secondaryAction} variant="secondary" />}
                </div>
            )}

            {children}

            {hints && hints.length > 0 && (
                <ul className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10 pt-8 border-t border-line-subtle w-full max-w-3xl text-left">
                    {hints.map((hint, i) => {
                        const HintIcon = hint.icon ?? Plus;
                        return (
                            <li key={i} className="flex sm:flex-col items-start gap-3">
                                <IconTile tone="neutral" size="md">
                                    <HintIcon strokeWidth={1.75} />
                                </IconTile>
                                <div className="min-w-0">
                                    <p className="text-ui font-semibold text-content">{hint.title}</p>
                                    {hint.description && (
                                        <p className="text-meta text-content-muted mt-0.5 leading-relaxed">
                                            {hint.description}
                                        </p>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default EmptyState;
